<template>
  <div id="DocumentArchive">
    <div class="mdl-grid">
      <div class="mdl-cell mdl-cell--8-col mdl-cell--8-col-tablet mdl-cell--12-col-phone docArchiveTitle">
        <MyHeaderPage
          :title="$t('documents.title')"
        />
      </div>
      <div
        class="mdl-cell mdl-cell--4-col mdl-cell--8-col-tablet mdl-cell--12-col-phone flex justify-end items-start docArchiveViewSwitch"
      >
        <SegmentedControl v-model="filters.view" :options="viewOptions" />
      </div>
    </div>
    <div id="LinesList">
      <div class="listLines">
        <div class="mdl-grid docArchiveFilters">
          <div class="mdl-cell mdl-cell--4-col mdl-cell--8-col-tablet mdl-cell--12-col-phone docArchiveSearch">
            <SearchField
              v-model="searchInput"
              :placeholder="$t('actions.text_search_placeholder')"
              @search="onSearch"
              @reset="onSearchReset"
            />
          </div>
          <div class="mdl-cell mdl-cell--2-col mdl-cell--2-col-tablet mdl-cell--12-col-phone docArchiveFilter">
            <div class="cdl-input">
              <select v-model="filters.categoryId" class="docArchiveCategory" :aria-label="$t('documents.table.category')">
                <option value="">{{ $t("documents.filters.allCategories") }}</option>
                <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                  {{ cat.name }}
                </option>
              </select>
            </div>
          </div>
          <div class="mdl-cell mdl-cell--2-col mdl-cell--2-col-tablet mdl-cell--12-col-phone docArchiveFilter">
            <div class="cdl-input">
              <input type="date" class="not-empty" v-model="filters.dateFrom" />
              <label>{{ $t("filter.from") }}</label>
            </div>
          </div>
          <div class="mdl-cell mdl-cell--2-col mdl-cell--2-col-tablet mdl-cell--12-col-phone docArchiveFilter">
            <div class="cdl-input">
              <input type="date" class="not-empty" v-model="filters.dateTo" />
              <label>{{ $t("filter.to") }}</label>
            </div>
          </div>
          <div class="mdl-cell mdl-cell--2-col mdl-cell--2-col-tablet mdl-cell--12-col-phone docArchiveFilter">
            <ActionButton
              :title="$t('documents.filters.reset')"
              custom-class="cdlButton docArchiveReset"
              @click="resetFilters"
            />
          </div>
        </div>
        <DocumentList
          :list="items"
          :view-loader="loaded"
          :view-error="hasErrorDownload"
          :load-error="hasErrorList"
          :error-message="$t('homePage.documentsBox.error')"
          :no-data-message="noDataMessage"
          :current-page="currentPage"
          :total-pages="totalPages"
          :downloading-id="downloadingId"
          @download="onDownload"
          @change-page="onChangePage"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch, computed, getCurrentInstance } from "vue";
import { useI18n } from "vue-i18n";
import { DocumentService } from "@/services/DocumentService";
import { usePaginatedList } from "@/composables/lists/usePaginatedList";
import { useListFilters } from "@/composables/lists/useListFilters";
import { useDownload } from "@/composables/useDownload";
import MyHeaderPage from "@/components/MyHeaderPage.vue";
import DocumentList from "@/components/Documents/DocumentList.vue";
import SegmentedControl from "@/components/Primitives/Buttons/SegmentedControl.vue";
import ActionButton from "@/components/Primitives/Buttons/ActionButton.vue";
import SearchField from "@/components/Primitives/Search/SearchField.vue";
import "@/assets/scss/LinesList.scss";
import "@/assets/scss/DocumentArchive.scss";

const { t, locale } = useI18n();
const { proxy } = getCurrentInstance();

// State locale - move before useFileDownload which references it
const hasErrorDownload = ref(false);

// Stato locale del campo di ricerca: aggiornato ad ogni tasto, pushato a filters.search solo su submit
const searchInput = ref("");

// Composables per i filtri
const { filters, resetFilters: resetFiltersComposable, hasActiveFilters } = useListFilters(
  {
    search: "",
    categoryId: "",
    dateFrom: "",
    dateTo: "",
    view: "all",
  },
  { ignore: ["view"] }
);

// Composables per la paginazione
const { items, currentPage, totalPages, loaded, hasError: hasErrorList, load: loadDocuments, changePage } = usePaginatedList(
  DocumentService.getDocuments,
  { pageSize: 10, errorResetMs: 0 } // errore di caricamento fisso in tabella fino al prossimo load
);

// Composables per il download
const { download: downloadFile, downloadingId } = useDownload({
  onError: () => {
    hasErrorDownload.value = true;
    setTimeout(() => {
      hasErrorDownload.value = false;
    }, 5000);
  },
});

// State locale
const categories = ref([]);

const noDataMessage = computed(() => {
  return hasActiveFilters.value
    ? t("documents.empty.filtered")
    : t("documents.empty.none");
});

const viewOptions = computed(() => [
  { value: "all", label: t("documents.tabs.all") },
  {
    value: "toDownload",
    label: t("documents.tabs.toDownload"),
  },
]);

// Metodi
function onSearch() {
  filters.search = searchInput.value;
}

function onSearchReset() {
  searchInput.value = "";
  filters.search = "";
}

function resetFilters() {
  searchInput.value = "";
  resetFiltersComposable();
}

function apiFilters() {
  return {
    search_string: filters.search,
    category: filters.categoryId,
    from_date: filters.dateFrom,
    to_date: filters.dateTo,
    read: filters.view === "toDownload" ? false : undefined,
    language: locale.value,
  };
}

async function getDocumentCategories() {
  const { data, error } = await DocumentService.getDocumentCategories({
    disable_pagination: true,
    language: locale.value,
  });
  if (error) console.error(error);
  categories.value = data?.items ?? [];
}

function onChangePage(page) {
  changePage(page);
  loadDocuments(apiFilters());
}

async function onDownload(doc) {
  const ok = await downloadFile(
    () => DocumentService.downloadDocument(doc.id, doc.category?.id),
    { id: doc.id, filename: doc.name }
  );
  if (ok) await loadDocuments(apiFilters());
}

// Watch per i filtri
watch(
  filters,
  () => {
    currentPage.value = 1;
    loadDocuments(apiFilters());
  },
  { deep: true }
);

// Cambio lingua: ricarica categorie e lista documenti
watch(locale, () => {
  currentPage.value = 1;
  getDocumentCategories();
  loadDocuments(apiFilters());
});

// onMounted
onMounted(() => {
  window.scrollTo(0, 0);
  proxy.$IsLoggedIn();
  getDocumentCategories();
  loadDocuments(apiFilters());
});
</script>
